import { gsap, Power3,Back } from "gsap";
import { StageLayer } from "@nolimitcity/slot-launcher/bin/StageLayer";
import { BLEND_MODES } from "pixi.js";
import { Helper } from "../../core/utils/Helper";
import { ISequencePlay, Sequence } from "../../core/displayobject/Sequence";
import { SpinePlayConfig, SpinePlayer } from "../../core/displayobject/SpinePlayer";
import { SanQuentin2 } from "../SanQuentin2";
import { SanEngineManager } from "../engine/SanEngineManager";
import { SlotGame } from "@nolimitcity/slot-game/bin/SlotGame";
import { TranslationConfig } from "../config/translate/TranslationConfig";
import { TextStyleConfig } from "../config/translate/TextStyleConfig";
import { GameSetting } from "../../core/setting/GameSetting";
import { SanSymbolData } from "../engine/Symbol/SanSymbolData";
import { GameAssets } from "../config/texture/GameAssets";
import { ResizeHelper } from "../../core/utils/ResizeHelper";

export interface OverlayItem {
    item: PIXI.DisplayObject;
    timeline:gsap.core.Timeline;
}

export class OverlayAnimation extends PIXI.Container implements StageLayer {

    private logoSp:PIXI.Sprite;
    private should_be_seen:boolean = true;
    private tl:gsap.core.Timeline;
    constructor() {
        super();

        this.logoSp = this.addChild(new PIXI.Sprite(SanQuentin2.assetLoader.getTextures(GameAssets.INTRO_LOGO)[0]));
        this.logoSp.anchor.set(0.5);
        this.logoSp.alpha = 1;

        this.sortableChildren = true;
        this.onResize();
    }

    private createGraphic(sizeX:number = 100,sizeY?:number,color?:number): PIXI.Graphics {
        if (!sizeY) sizeY = sizeX;
        return this.addChild(Helper.createBasicGraphic(
            color != undefined ? color : 0xFFFFFF, 1,
            -sizeX * 0.5, -sizeY * 0.5,
            sizeX, sizeY));;
    }

    public playStandAloneSequence(texture:PIXI.Texture[], conf:ISequencePlay = {},layer?:PIXI.Container):OverlayItem{
        const item:Sequence = layer ? layer.addChild(new Sequence(texture)) : this.addChild(new Sequence(texture));
        item.anchor.set(0.5);

        const tl:gsap.core.Timeline = Helper.temporaryItemTl(item);
        
        tl.add(item.play(conf));
        
        return {item:item,timeline:tl};
    }

    public addChild<TChildren extends PIXI.DisplayObject[]>(...child: TChildren): TChildren[0]{
        super.addChild(...child);
        this.sortChildren();
        return child[0];
    }

    public playStandAloneSpine(path:string, conf:SpinePlayConfig,layer?:PIXI.Container ):OverlayItem{
        const spine:SpinePlayer = layer ? layer.addChild(new SpinePlayer(path)) : this.addChild(new SpinePlayer(path))
        const tl:gsap.core.Timeline = Helper.temporaryItemTl(spine);
        
        tl.add(spine.play(conf));

        return {item:spine,timeline:tl};
    }

    public playSpine(target: PIXI.DisplayObject, path: string, conf: SpinePlayConfig, stay: boolean = false, cont?: PIXI.Container, initVisible:boolean = true): OverlayItem {
        const item: SpinePlayer = cont ? cont.addChild(new SpinePlayer(path, initVisible)) : this.addChild(new SpinePlayer(path, initVisible))
        const tl: gsap.core.Timeline = stay ? new gsap.core.Timeline({
            onUpdate: () => {
                if (target) {
                    item.position = Helper.copyLocalPosition(item, target)
                } else {
                    tl.kill();
                    item.destroy();
                }
                conf.onUpdate ? conf.onUpdate() : undefined;
            }
        }) : this.createItemTlToFollow(target, item);

        item.position = Helper.copyLocalPosition(item, target)
        tl.add(item.play(conf));

        return { item: item, timeline: tl };
    }

    public addItemToOverlay(target:PIXI.DisplayObject, item:PIXI.DisplayObject):PIXI.DisplayObject{
        this.addChild(item);
        item.position = Helper.copyLocalPosition(item,target)
        return item;
    }
  
    public playSequence(target:PIXI.DisplayObject, texture:PIXI.Texture[], conf:ISequencePlay = {},layer?:PIXI.Container):OverlayItem{
        const item:Sequence = layer ? layer.addChild(new Sequence(texture)) : this.addChild(new Sequence(texture));
        item.anchor.set(0.5);

        const tl:gsap.core.Timeline = this.createItemTlToFollow(target,item);

        tl.add(item.play(conf));
        
        return {item:item,timeline:tl};
    }

    public playValueText(target: PIXI.DisplayObject, val: number,waysValue:number): gsap.core.Timeline {

        const currValue:string  = SlotGame.apiPlugin.currency.formatValue(val)
        const txt: PIXI.Text | PIXI.BitmapText = this.addChild(new PIXI.BitmapText(currValue, TextStyleConfig.BIT_COUNTER_WIN));
        txt.anchor = new PIXI.Point(0.5, 0.4);
        txt.fontSize = 18;

        const txtWays = this.addChild(new PIXI.Text(""));
        txtWays.style = TextStyleConfig.XWAYS_WIN;
        txtWays.anchor.set(0.5, 0.4);
        txtWays.pivot.y = -60;
        txtWays.text = waysValue.toString() + " " +  GameSetting.translate(TranslationConfig.WAY_TEXT(waysValue));
        txtWays.updateTransform();
        txtWays.scale.set(0.7)

        txt.zIndex = 5000;
        this.sortChildren();

        const startPos: PIXI.Point = Helper.copyLocalPosition(txt, target)
        const heightYDirr: number = 60;
        startPos.x += SanSymbolData.DEFAULT_DIM.WIDTH/4;
        txt.alpha = 0;
        txtWays.alpha = 0;
      
        txt.position = startPos;
        txtWays.position = startPos;

        const maxWidth = SanSymbolData.DEFAULT_DIM.WIDTH*2;
        let targetScale:number = 1;

        if(txt.width > maxWidth) targetScale = maxWidth/txt.width;
        if(txtWays.width > maxWidth) targetScale = Math.min(targetScale,maxWidth/txtWays.width);

        txt.scale.set(targetScale);
        txtWays.scale.set(targetScale);
        txtWays.zIndex = 500;

        const up: number = startPos.y - heightYDirr;

        const moveDurr: number = 1;
        const alphaDurr: number = 0.2;
    
        const tl: gsap.core.Timeline = new gsap.core.Timeline({
            onComplete: () => { 
                if(txt && txt.transform) txt.destroy({ children: true });
                if(txtWays && txtWays.transform) txtWays.destroy({ children: true });
            }
        });

        const animate:PIXI.DisplayObject[] = [txt,txtWays];

        tl.to(animate, { alpha: 1, duration: alphaDurr, ease: "Power3.easeIn" }, 0);
        tl.to(animate, { y: up, duration: moveDurr }, 0);
        // animate.forEach(item => {
        //     tl.to(item.scale, { x: 0.8, y: 0.8, duration: scaleDurr }, moveDurr-alphaDurr*2);
        // })
        tl.to(animate, { duration: alphaDurr*2, alpha: 0 }, "-="+alphaDurr*2);
        return tl;
    }

    public playPlusFS(val: number, target:PIXI.DisplayObject): gsap.core.Timeline {

        const txtWays = this.addChild(new PIXI.Text(""));
        txtWays.style = TextStyleConfig.BAR_REVEALER_TEXT;
        txtWays.style.align = "center";
        txtWays.anchor.set(0.5, 0.4);
        txtWays.pivot.y = -60;
        txtWays.text = val.toString() + "+\n" + "Free Spins"
        txtWays.updateTransform();

        const startPos: PIXI.Point = Helper.copyLocalPosition(txtWays, target)
        const heightYDirr: number = 40;

        txtWays.position = startPos;

        const moveDurr: number = 1.2;
        const alphaDurr: number = 0.4;
        const up: number = startPos.y - heightYDirr;

        const tl: gsap.core.Timeline = new gsap.core.Timeline({
            onComplete: () => { 
                if(txtWays && txtWays.transform) txtWays.destroy({ children: true });
            }
        });

        tl.to(txtWays, { alpha: 1, duration: alphaDurr/2, ease: "Power3.easeIn" }, 0);
        tl.to(txtWays, { y: up, duration: moveDurr }, 0);
        tl.to(txtWays, { duration: alphaDurr, alpha: 0 }, "-=" + alphaDurr)

        return tl;
    }

    public flashOutGFX(target:PIXI.DisplayObject,sizeX?:number,sizeY?:number, durr:number = 0.5, color?:number):OverlayItem{

        const item:PIXI.DisplayObject = this.createGraphic(sizeX, sizeY,color);
        const tl:gsap.core.Timeline = this.createItemTlToFollow(target,item);

        tl.fromTo(item,{alpha:1},{alpha:0, duration:durr});
        return {item:item,timeline:tl};
    }

    public flashGFX(target:PIXI.DisplayObject,sizeX?:number,sizeY?:number, durr:number = 0.5, color?:number):OverlayItem{

        const item:PIXI.DisplayObject = this.createGraphic(sizeX, sizeY,color);
        const tl:gsap.core.Timeline = this.createItemTlToFollow(target,item);

        tl.fromTo(item,{alpha:0},{alpha:1, duration:durr/2});
        tl.to(item,{alpha:0, duration:durr/2});
        return {item:item,timeline:tl};
    }

    public useItemAsOverlay(target: PIXI.DisplayObject, item: PIXI.DisplayObject): OverlayItem {
        const tl: gsap.core.Timeline = this.createItemTlToFollow(target, item);
        this.addChild(item);
        return { item: item, timeline: tl };
    }

    private createItemTlToFollow(target:PIXI.DisplayObject, item:PIXI.DisplayObject):gsap.core.Timeline{
        const tl = new gsap.core.Timeline({
            onUpdate:() =>{
                if(!target || !target.parent){
                    remove();
                }else if(item.transform){
                    item.position = Helper.copyLocalPosition(item,target)
                }
            },
            onComplete:() => remove()
        });

        item.position = Helper.copyLocalPosition(item,target)

        function remove(){
            tl.kill();
            item.transform && item.destroy();
            target = undefined!;
        }

        return tl;
    }

    public playIntroDarkFade(durr:number):gsap.core.Timeline{
        const introGFX = this.createGraphic(2000,2000,0x000000);

        introGFX.position.set(360,360);
        introGFX.zIndex = -2;

        const tl = gsap.timeline({onComplete:() => {
            introGFX.destroy();
        }})
        tl.to(introGFX,{alpha:0, duration:durr},0);
        return tl;
    }

    public removeNonUsedAnimations():void{
        this.children.forEach(s => {if(s.name != "mask") s.destroy()});
    }

    public flashMystery(reel:number,row:number){
        const toPosition: PIXI.Point = SanQuentin2.reelEngineManager.getSymPos(reel,row);
        
        let sp:Sequence = this.addChild(new Sequence(SanQuentin2.assetLoader.getTextures("M/4/M_normal")));
        sp.blendMode = BLEND_MODES.ADD;
        sp.position = toPosition;
        sp.anchor.set(0.5,0.5);
        
        gsap.fromTo(sp,{alpha:1},{alpha:0,duration:2}).eventCallback("onComplete", () =>{
            sp.destroy();
        })
    }

    public winLvlAnimation(lvl:number = 0):gsap.core.Timeline{

        const tl = Helper.setTimeline();

        if(lvl >= 1){
            let seqI = this.playStandAloneSequence(SanQuentin2.assetLoader.getTextures("Win_Xplod_A"));// this.addChild(createSeq());
            seqI.item.scale.set(Math.min(lvl+1,2));
            (seqI.item as Sequence).anchor.set(0.5);
            (seqI.item as Sequence).blendMode = BLEND_MODES.ADD;
            seqI.item.position.set(440,320);
            tl.add(seqI.timeline.play(),0);
        }

        if(lvl >= 2){
            let seqI = this.playStandAloneSequence(SanQuentin2.assetLoader.getTextures("Win_Xplod_B"));
            seqI.item.scale.set(Math.min(lvl+1,2));
            (seqI.item as Sequence).anchor.set(0.5);
            (seqI.item as Sequence).blendMode = BLEND_MODES.ADD;
            seqI.item.position.set(400,310);
            tl.add(seqI.timeline.play(),0);
        } 

        return this.tl;
    }

    public hideLogo():void{
        this.logoSp.alpha = 0;
        this.should_be_seen = false;
    }

    public introApplyLogo():void{
        const durr = 0.5;
        this.should_be_seen = true;
        gsap.fromTo(this.logoSp,{alpha:0},{alpha:1,duration:durr})
        this.onResize();
    }

    public onResize():void{

        if(this.should_be_seen){
            this.logoSp.visible = !ResizeHelper.isIPadd;
        }

        if(ResizeHelper.isLandscape){
            this.logoSp.position.set(-185,125)
            this.logoSp.scale.set(1);
        }else{
            this.logoSp.position.set(100,-175);
            this.logoSp.scale.set(0.8);
        }
        this.scale = SanEngineManager.dim.scale;
    }
}